/* eslint-env node */

import {
	WebSocketGateway,
	CommentsService,
	TrackChangesService,
	RevisionHistoryService,
} from '@ckeditor/ckeditor-cloud-services-collaboration';

import { Token } from 'ckeditor5';

/**
 * Util, which firstly connects to CS, then saves users and comments to persistance storage.
 *
 * @param {String} documentId Document id.
 * @param {Array} changes
 * @param {Object} changes[n].user User data.
 * @param {Object} changes[n].comments List of comment threads.
 * @param {Object} changes[n].suggestions List of changes and suggestions.
 * @param {Object} config CS configuration.
 * @returns {Promise}
 */
export async function generateCommentsAndChanges(documentId, changes, config) {
	const parentRecord = openRun();
	const wsGatewayPromises = [];
	const collaborationDataPromises = [];

	// Create WebSocket gateways for users from the initial data to make sure
	// that user objects are available in the document.
	for (const { user } of changes) {
		const wsGatewayPromise = createGateway(user, config, parentRecord);

		wsGatewayPromises.push(wsGatewayPromise);
	}

	const wsGateways = await Promise.all(wsGatewayPromises);
	const hasComments = await documentHasComments(
		wsGateways[0],
		documentId,
		parentRecord
	);

	if (hasComments) {
		return;
	}

	for (const entry of changes) {
		// Get the WebSocket gateway for the specific user to make sure that the suggestion data was saved
		// in the server with correct author ID.
		const wsGateway = wsGateways.find(
			(gateway) => gateway._socketAuth.userId === entry.user.id
		);

		if (entry.comments) {
			const comments = generateComments(
				wsGateway,
				documentId,
				entry.comments,
				parentRecord
			);

			collaborationDataPromises.push(comments);
		}

		if (entry.suggestions) {
			const suggestions = generateChanges(
				wsGateway,
				documentId,
				entry.suggestions,
				parentRecord
			);

			collaborationDataPromises.push(suggestions);
		}

		if (entry.revisions) {
			const revisions = generateRevisions(
				wsGateway,
				documentId,
				entry.revisions,
				parentRecord
			);

			collaborationDataPromises.push(revisions);
		}
	}

	return Promise.all(collaborationDataPromises);
}

/**
 * Util, which saves comments to persistance storage.
 *
 * @param {Object} wsGateway CS connection gateway.
 * @param {String} documentId Document id.
 * @param {Array} initialData List of comment threads.
 * @param {Object|null} parentRecord Run to record the WS timings under, or null when tracking is off.
 * @returns {Promise}
 */
function generateComments(wsGateway, documentId, initialData, parentRecord) {
	const service = new CommentsService(documentId);

	return trackWs(
		parentRecord,
		'CommentsService.connect',
		service.connect(wsGateway)
	)
		.then(() => {
			const promises = [];

			initialData.forEach((comment) => {
				promises.push(
					service.addComment(
						comment.thread,
						comment.commentId,
						comment.content
					)
				);
			});

			return trackWs(
				parentRecord,
				'CommentsService.addComment',
				Promise.all(promises),
				{ 'demo.operations': promises.length }
			);
		})
		.then(() => {
			service.disconnect();
		})
		.catch((err) => {
			service.disconnect();

			throw err;
		});
}

/**
 * Util, which saves changes and suggestions to persistance storage.
 *
 * @param {Object} wsGateway CS connection gateway.
 * @param {String} documentId Document id.
 * @param {Array} initialData List of Track Changes suggestions and updates.
 * @param {Object|null} parentRecord Run to record the WS timings under, or null when tracking is off.
 * @returns {Promise}
 */
function generateChanges(wsGateway, documentId, initialData, parentRecord) {
	const service = new TrackChangesService(documentId);

	return trackWs(
		parentRecord,
		'TrackChangesService.connect',
		service.connect(wsGateway)
	)
		.then(() => {
			const promises = [];

			initialData.forEach((change) => {
				promises.push(
					service.add(
						change.id,
						change.type,
						change.data,
						change.originalSuggestionId,
						change.attributes
					)
				);
			});

			return trackWs(
				parentRecord,
				'TrackChangesService.add',
				Promise.all(promises),
				{ 'demo.operations': promises.length }
			);
		})
		.then(() => {
			service.disconnect();
		})
		.catch((err) => {
			service.disconnect();

			throw err;
		});
}

/**
 * Util, which saves changes and suggestions to persistance storage.
 *
 * @param {Object} wsGateway CS connection gateway.
 * @param {String} documentId Document id.
 * @param {Array} initialData Array of revisions.
 * @param {Object|null} parentRecord Run to record the WS timings under, or null when tracking is off.
 * @returns {Promise}
 */
function generateRevisions(wsGateway, documentId, initialData, parentRecord) {
	const service = new RevisionHistoryService(documentId);

	return trackWs(
		parentRecord,
		'RevisionHistoryService.connect',
		service.connect(wsGateway)
	)
		.then((response) => {
			return trackWs(
				parentRecord,
				'RevisionHistoryService.updateRevisions',
				service.updateRevisions(initialData, response.requestId),
				{ 'demo.operations': initialData.length }
			);
		})
		.then(() => {
			service.disconnect();
		})
		.catch((err) => {
			service.disconnect();

			throw err;
		});
}

/**
 * Checks if the data for given document was already generated and sent to CS.
 *
 * @param {Object} wsGateway WebSocket Gateway instance.
 * @param {String} documentId Document id.
 * @param {Object|null} parentRecord Run to record the WS timing under, or null when tracking is off.
 * @returns {Promise}
 */
function documentHasComments(wsGateway, documentId, parentRecord) {
	const service = new CommentsService(documentId);

	return trackWs(
		parentRecord,
		'documentHasComments',
		service.connect(wsGateway)
	).then((response) => response.length > 0);
}

/**
 * @param {Object} user - Requested user data
 * @param {Object} config CS configuration.
 * @param {Object|null} parentRecord Run to record the WS timings under, or null when tracking is off.
 * @returns {Promise}
 */
function createGateway(user, config, parentRecord) {
	user = Object.assign({ id: uid() }, user);
	const tokenUrl = buildUserTokenUrl(config.tokenUrl, user);
	const attributes = { 'demo.user': user.name || user.id };

	return trackWs(
		parentRecord,
		'Token.create',
		Token.create(tokenUrl),
		attributes
	).then((token) =>
		trackWs(
			parentRecord,
			'WebSocketGateway.connect',
			WebSocketGateway.connect(token, config.webSocketUrl),
			attributes
		)
	);
}

/**
 * Create user token url, which is used in the Letters to CS connection.
 *
 * @param {String} baseUrl - Main part of the CS URL.
 * @param {Object} user - Requested user data
 * @returns {String} - Ready to use tokenUrl.
 */
function buildUserTokenUrl(baseUrl, user) {
	const url = baseUrl.indexOf('?') > -1 ? `${baseUrl}&` : `${baseUrl}?`;
	return (
		url +
		Object.keys(user)
			.filter((key) => user[key])
			.map((key) => `user.${key}=${user[key]}`)
			.join('&')
	);
}

/**
 * Create simple UID version.
 *
 * @param {String} hashLength - Length of the generated ID.
 * @returns {String} - Unique ID.
 */
function uid(hashLength = 32) {
	if (hashLength < 8) {
		hashLength = 8;
	}
	let uuid = 'e'; // Make sure that id does not start with number.
	const loopsQtty =
		hashLength % 4 ? Math.floor(hashLength / 4) + 1 : hashLength;

	for (let i = 0; i < loopsQtty; i++) {
		uuid += Math.floor((1 + Math.random()) * 0x10000)
			.toString(16)
			.substring(1);
	}

	return uuid.substr(0, hashLength);
}

/* ------------------------------------------------------------------------ *
 * Timings.
 *
 * This build is shared by every internal demo, so the tracking below is inert
 * unless the caller opted in. Only the RTC collaboration demo does: it wraps its
 * `generateCommentsAndChanges()` call in `measure()` (see
 * themes/ckecosystem/layout/demo/demo-helpers/demo-sentry-spans.js), which opens
 * a run on a shared buffer and, once Sentry has finished loading, replays the
 * whole tree as backdated spans.
 *
 * Nothing here knows about Sentry - the demo bootstrap routinely finishes before
 * the SDK does, so this only appends to that buffer. With no run open `trackWs()`
 * hands the original promise straight back and nothing is allocated.
 * ------------------------------------------------------------------------ */

/**
 * The run the caller opened around this bootstrap, if there is one.
 *
 * Read once, synchronously, at the top of the run and then passed down by hand:
 * the buffer tracks the open run on a plain stack, so it is already back to null
 * by the time the first `await` resolves.
 *
 * @returns {Object|null} Parent for the WS records, or null when tracking is off.
 */
function openRun() {
	const buffer =
		typeof window !== 'undefined' ? window.__ckDemoTimings : null;

	return buffer ? buffer.current : null;
}

/**
 * Times one Cloud Services round trip. Returns the promise unchanged when
 * tracking is off, so the shared demos pay nothing for this.
 *
 * @param {Object|null} parentRecord Run to record under, or null when off.
 * @param {String} name Span name, and what the dashboard groups by. Keep ids and
 * counts out of it - they belong in `attributes`, or every round trip ends up in
 * a bucket of its own.
 * @param {Promise} promise The round trip to time.
 * @param {Object} [attributes] Extra attributes for this round trip.
 * @returns {Promise} The same result, resolved or rejected as before.
 */
function trackWs(parentRecord, name, promise, attributes) {
	if (!parentRecord) {
		return promise;
	}

	const record = {
		name,
		op: 'demo.rtc.ws',
		parent: parentRecord,
		startedAt: now(),
		endedAt: null,
		ok: null,
		attributes,
	};

	window.__ckDemoTimings.records.push(record);

	const settle = (ok) => {
		record.endedAt = now();
		record.ok = ok;
	};

	return promise.then(
		(value) => {
			settle(true);

			return value;
		},
		(error) => {
			settle(false);

			throw error;
		}
	);
}

/**
 * @returns {Number} Milliseconds since the epoch, from the monotonic clock.
 */
function now() {
	return performance.timeOrigin + performance.now();
}
